
import { AfterViewInit, Component, OnDestroy, ChangeDetectionStrategy, ViewChild, HostListener } from '@angular/core';
import { ChatModule } from './chat-ui/chat.module';
import { AppPermissionService } from './chat-ui/services/app-permission.service';
import { ChatEventsService } from './chat-ui/services/chat-events.service';
import { ChatConfigRequest, ChatUserInfo } from './chat-ui/models/chat.models';
import { CHAT_INDEXED_DB_CONFIG } from './chat-ui/enums/event.enum';
import { Subject, takeUntil } from 'rxjs';
import { getBearerToken, persistBearerToken } from './auth-token';
import { getRuntimeConfig } from './runtime-config';
import { FileUtilsService } from './chat-ui/services/file-utils.service';
import { Quote } from './chat-ui/models/ai-quote-chat.models';
import { AiQuoteChatComponent } from './chat-ui/components/ai-quote/ai-quote-chat/ai-quote-chat.component';

type RegionConfig = {
  regionCode: string;
  chatServiceUrl: string;
};

type NativeChatBridge = {
  postMessage?: (message: string) => void;
  onMessage?: (message: string) => void;
  onChatbotEvent?: (message: string) => void;
};

type ChatbotWindow = Window & {
  AndroidBridge?: NativeChatBridge;
  ChatbotBridge?: NativeChatBridge;
};

const DEFAULT_UPLOAD_OPTIONS_ENABLED = true;

enum ChatProfileId {
  Global = 'global'
}

enum QueryParam {
  BuildingEntityList = 'buildingEntityList',
  ChatInputPlaceholderText = 'chatInputPlaceholderText',
  IsAndroid = 'isAndroid',
  IsCCU = 'isCCU',
  IsDarkTheme = 'isDarkTheme',
  IsMobileApp = 'isMobileApp',
  PreloadOnly = 'preloadOnly',
  ProfileId = 'profileId',
  RegionCode = 'regionCode',
  RegionId = 'regionId',
  SendIconEnabled = 'sendIconEnabled',
  SiteId = 'siteId',
  TaskData = 'taskData',
  UploadOptionsEnabled = 'uploadOptionsEnabled',
  WidgetMode = 'widgetMode',
  ZoneId = 'zoneId'
}

enum WidgetMode {
  Chat = 'chat',
  AiQuoteChat = 'ai-quote-chat'
}

enum BooleanQueryValue {
  True = 'true',
  One = '1',
  Yes = 'yes'
}

enum MessageSource {
  ChatBotWebapp = 'ai-chat',
  CcuApp = 'ccu-app',
  FacilisightApp = 'facilisight-app',
  OccupantApp = 'occupant-app',
  PortalUiComponents = 'portal-ui-components'
}

enum ChatbotEvent {
  BuildingEntityList = 'building-entity-list',
  CameraPermissionStatus = 'camera-permission-status',
  ChartGenerated = 'chartGenerated',
  ChatEvent = 'chat-event',
  Close = 'close',
  ConfigUpdate = 'config-update',
  Expanded = 'expanded',
  LayoutChange = 'layout-change',
  MicrophonePermissionStatus = 'microphone-permission-status',
  Minimized = 'minimized',
  Maximized = 'maximized',
  DefaultView = 'default-view',
  RecommendCharts = 'recommendCharts',
  RequestCameraPermission = 'request-camera-permission',
  RequestMicrophonePermission = 'request-microphone-permission',
  SupportClick = 'support-click',
  UserInfo = 'user-info',
  VariationGenerated = 'variationGenerated',
  WebappLoaded = 'webapp-loaded',
  InternalLinkRedirection = 'internal-link-redirection',
  DownloadFile = 'download-file',
  Timeout = 'timeout',
  QuoteFinalized = 'quote-finalized',
  Error = 'error',
  Restore = 'restore'
}

enum ParentMessageType {
  BuildingEntityList = 'BUILDING_ENTITY_LIST'
}

enum ChatLayoutClass {
  Container = 'chat-container',
  Maximize = 'maximize-chat',
  ImagePreviewOpen = 'image-preview-open',
  CameraDialogOpen = 'camera-dialog-open',
  Collapse = 'collapse-chat',
  MobileCollapse = 'mobile-collapse-chat'
}

enum ChatActionType {
  NewDashboardNavigate = 'NEW_DASHBOARD_NAVIGATE'
}

@Component({
  selector: 'app-root',
  imports: [ChatModule],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app.component.scss'
})
export class AppComponent implements AfterViewInit, OnDestroy {
  // Reference used to un-collapse the AI Quote chat when the host requests a restore.
  @ViewChild(AiQuoteChatComponent) private aiQuoteChatComponent?: AiQuoteChatComponent;
  readonly params = new URLSearchParams(window.location.search);
  private readonly appConfig = getRuntimeConfig();
  private readonly regionConfig = this.getRegionConfig();

  chatConfig: ChatConfigRequest = this.getChatConfig();
  readonly bearerToken = getBearerToken();
  readonly isCCU = this.getBooleanParam(QueryParam.IsCCU, false);
  readonly isMobileApp = this.getBooleanParam(QueryParam.IsMobileApp, false);
  readonly isAndroid = this.getBooleanParam(QueryParam.IsAndroid, false);
  readonly sendIconEnabled = this.getBooleanParam(QueryParam.SendIconEnabled, true);
  readonly uploadOptionsEnabled = this.getBooleanParam(QueryParam.UploadOptionsEnabled, DEFAULT_UPLOAD_OPTIONS_ENABLED);
  readonly isDarkTheme = this.getBooleanParam(QueryParam.IsDarkTheme, false);
  readonly preloadOnly = this.getBooleanParam(QueryParam.PreloadOnly, false);
  readonly widgetMode = this.getParam(QueryParam.WidgetMode, WidgetMode.Chat);
  readonly chatInputPlaceholderText = this.getParam(QueryParam.ChatInputPlaceholderText, '');
  generativeWidgetReady = this.widgetMode === WidgetMode.Chat;
  buildingEntityList = this.getJsonParam<any[]>(QueryParam.BuildingEntityList, []);
  chatUserInfo?: ChatUserInfo;
  chatMinimized = false;
  /**
   * Tracks whether keyboard tab navigation focus is active in the parent shell (topbar/sidebar).
   * Used to know whether a click inside this iframe needs to clear the parent outline.
   */
  isTabNavActive = false;
  private observedChatContainer?: Element;
  private chatContainerObserver?: MutationObserver;
  private chatContainerResizeObserver?: ResizeObserver;
  private chatLayoutSettleTimeout?: number;
  private chatLayoutState = '';
  private chatLayoutCollapsed = false;
  private lastExpandedLayoutPayload?: any;
  private readonly unsubscribe = new Subject<void>();
  private readonly parentMessageHandler = (event: MessageEvent) => this.onParentMessage(event);
  private readonly windowResizeHandler = () => this.postCurrentChatLayoutState();
  private readonly chatTransitionEndHandler = () => this.postCurrentChatLayoutState(true);
  private readonly chatInputEventHandler = () => {
    if (this.observedChatContainer) {
      this.scheduleChatLayoutState(this.observedChatContainer);
    }
  };
  private readonly chatContainerResizeHandler = () => {
    if (this.observedChatContainer) {
      this.scheduleChatLayoutState(this.observedChatContainer);
    }
  };

  /** Initializes token persistence, host-app event bridges, and parent message handling. */
  constructor(
    private readonly chatEventsService?: ChatEventsService,
    private readonly appPermissionService?: AppPermissionService,
    private readonly fileUtilsService?: FileUtilsService
  ) {
    persistBearerToken(this.bearerToken);
    this.applyTaskData();
    this.forwardChatEventsToParent();
    this.forwardCameraPermissionRequestsToParent();
    this.forwardMicPermissionRequestsToParent();
    this.forwardDownloadEventsToParent();
    window.addEventListener('message', this.parentMessageHandler);
  }

  /** Sends support-link clicks from the embedded chat UI to the host app. */
  onSupportClick(event: unknown): void {
    this.postToParent({ source: MessageSource.ChatBotWebapp, event: ChatbotEvent.SupportClick, payload: event });
  }

  /** Forwards explicit chat-ui action events emitted by the embedded chat component. */
  onChatEvent(event: unknown): void {
    this.postToParent({ source: MessageSource.ChatBotWebapp, event: ChatbotEvent.ChatEvent, payload: event });
  }

  /** Notifies the host app that the chat window requested to close. */
  onChatClose(): void {
    this.postToParent({ source: MessageSource.ChatBotWebapp, event: ChatbotEvent.Close });
  }

  /** Notifies the host app that the AI quote chat window requested to close. */
  onAIQuoteChatClose(event: any): void {
    this.postToParent({ source: MessageSource.ChatBotWebapp, event: ChatbotEvent.Close, payload: { conversationId: event } });
  }

  /** Notifies the host app that the chat window was minimized. */
  onChatMinimized(): void {
    this.chatMinimized = true;
    this.postToParent({ source: MessageSource.ChatBotWebapp, event: ChatbotEvent.Minimized });
  }

  /** Notifies the host app that the chat window was reset to default view. */
  onDefaultView(): void {
    this.chatMinimized = false;
    this.postToParent({ source: MessageSource.ChatBotWebapp, event: ChatbotEvent.DefaultView });
  }
  
  /** Notifies the host app that the chat window was maximized. */
  onChatMaximized(): void {
    this.chatMinimized = false;
    this.postToParent({ source: MessageSource.ChatBotWebapp, event: ChatbotEvent.Maximized });
  }

  /** Notifies the host app that the chat window was expanded. */
  onChatExpanded(): void {
    this.chatMinimized = false;
    this.postToParent({ source: MessageSource.ChatBotWebapp, event: ChatbotEvent.Expanded });
  }

  /** Forwards generated chart data from generative widgets to the host app. */
  onChartGenerated(event: unknown): void {
    this.postToParent({ source: MessageSource.ChatBotWebapp, event: ChatbotEvent.ChartGenerated, payload: event });
  }

  /** Forwards chart recommendation events from generative widgets to the host app. */
  onRecommendCharts(event: unknown): void {
    this.postToParent({ source: MessageSource.ChatBotWebapp, event: ChatbotEvent.RecommendCharts, payload: event });
  }

  /** Forwards generated chart variation data from command-layout widgets to the host app. */
  onVariationGenerated(event: unknown): void {
    this.postToParent({ source: MessageSource.ChatBotWebapp, event: ChatbotEvent.VariationGenerated, payload: event });
  }

  onInternalLinkRedirection(url: string): void {
    this.postToParent({ source: MessageSource.ChatBotWebapp, event: ChatbotEvent.InternalLinkRedirection, payload: { url } });
  }

  /** Notifies the host app that the chat window has timed out. */
  onSessionTimeout(event: any): void {
    this.postToParent({ source: MessageSource.ChatBotWebapp, event: ChatbotEvent.Timeout, payload: { status: event?.status ?? '', message: event?.message ?? '' } });
  }

  /** Starts layout observation after the chat DOM is rendered. */
  ngAfterViewInit(): void {
    if (this.widgetMode === WidgetMode.Chat || this.widgetMode === WidgetMode.AiQuoteChat) {
      this.observeChatLayout();
    }
    this.postCcuWebappReady();
  }

  /** Tears down DOM observers, parent message listeners, and RxJS subscriptions. */
  ngOnDestroy(): void {
    this.chatContainerObserver?.disconnect();
    this.chatContainerResizeObserver?.disconnect();
    if (this.chatLayoutSettleTimeout) {
      window.clearTimeout(this.chatLayoutSettleTimeout);
    }
    this.observedChatContainer?.removeEventListener('transitionend', this.chatTransitionEndHandler);
    this.observedChatContainer?.removeEventListener('input', this.chatInputEventHandler, true);
    window.removeEventListener('resize', this.windowResizeHandler);
    window.removeEventListener('message', this.parentMessageHandler);
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  /** Reads a query parameter and falls back when the parameter is missing. */
  private getParam(key: string, defaultValue?: any): any {
    if (!key) {
      return defaultValue;
    }

    return this.params.get(key) ?? defaultValue;
  }

  /** Parses a query parameter as a boolean, supporting true/1/yes values. */
  private getBooleanParam(key: string, fallback: boolean): boolean {
    const value = this.params.get(key);
    if (value === null || value === '') {
      return fallback;
    }

    return [BooleanQueryValue.True, BooleanQueryValue.One, BooleanQueryValue.Yes].includes(value.toLowerCase() as BooleanQueryValue);
  }

  /** Selects the runtime region config from regionId, regionCode, or the default region. */
  private getRegionConfig(): RegionConfig {
    const configByRegion = this.appConfig as unknown as Record<string, RegionConfig>;
    const regionCode = this.params.get(QueryParam.RegionCode);
    const regionId = this.params.get(QueryParam.RegionId) || this.getRegionIdByCode(regionCode, configByRegion) || this.appConfig.defaultRegion;

    return configByRegion[regionId] || configByRegion[this.appConfig.defaultRegion];
  }

  /** Resolves a region id by matching the supplied region code against runtime config. */
  private getRegionIdByCode(regionCode: string | null, configByRegion: Record<string, RegionConfig>): string | undefined {
    if (!regionCode) {
      return undefined;
    }

    return Object.keys(configByRegion).find((key) => configByRegion[key]?.regionCode === regionCode);
  }

  /** Builds the chat-ui config from iframe query params and runtime region config. */
  private getChatConfig(): ChatConfigRequest {
    const excludedParamNames = new Set([
      ...Object.values(QueryParam),
      'appName',
      'auth',
      'bearerToken',
    ]);
    const requestParams = Object.fromEntries(
      Array.from(this.params).filter(([key]) => !excludedParamNames.has(key))
    );
    const siteId = this.getParam(QueryParam.SiteId, undefined);
    const zoneId = this.getParam(QueryParam.ZoneId, undefined);
    const profileId = this.getParam(QueryParam.ProfileId, ChatProfileId.Global);

    return {
      ...requestParams,
      chatUrl: this.regionConfig.chatServiceUrl,
      regionCode: this.getParam(QueryParam.RegionCode, this.regionConfig.regionCode),
      profileId,
      siteId,
      zoneId
    };
  }

  private normalizeUserInfo(value: any): ChatUserInfo | undefined {
    if (!value || typeof value !== 'object') {
      return undefined;
    }

    return {
      firstName: value?.firstName ?? '',
      lastName: value?.lastName ?? '',
      eMail: value?.eMail ?? '',
      phone: value?.phone ?? ''
    };
  }

  /** Applies optional task data from query params onto the chat config. */
  private applyTaskData(): void {
    const taskData = this.getJsonParam(QueryParam.TaskData, undefined);
    if (taskData) {
      this.chatConfig.taskData = taskData;
    }
  }

  /** Parses a JSON query parameter and returns the fallback on missing or invalid input. */
  private getJsonParam<T>(key: string, fallback: T): T {
    const value = this.params.get(key);
    if (!value) {
      return fallback;
    }

    try {
      return JSON.parse(value) as T;
    } catch {
      return fallback;
    }
  }

  /** Sends a structured event payload to web iframe hosts and native WebView bridges. */
  private postToParent(payload: unknown): void {
    if (this.isCCU) {
      this.postToNativeBridge(payload);
    } else {
      window.parent?.postMessage(payload, '*');
    }
  }

  /** Notifies the CCU native host once the chat webapp view is initialized. */
  private postCcuWebappReady(): void {
    if (!this.isCCU) {
      return;
    }

    window.setTimeout(() => {
      this.postToParent({
        source: MessageSource.ChatBotWebapp,
        event: ChatbotEvent.WebappLoaded
      });
    }, 0);
  }

  /** Sends events to native Android WebView hosts when they expose a JavaScript bridge. */
  private postToNativeBridge(payload: unknown): void {
    const nativeBridge = (window as ChatbotWindow).AndroidBridge ?? (window as ChatbotWindow).ChatbotBridge;
    if (!nativeBridge) {
      return;
    }

    let message: string;
    try {
      message = JSON.stringify(payload);
    } catch {
      return;
    }

    if (typeof nativeBridge.postMessage === 'function') {
      nativeBridge.postMessage(message);
      return;
    }

    if (typeof nativeBridge.onMessage === 'function') {
      nativeBridge.onMessage(message);
      return;
    }

    if (typeof nativeBridge.onChatbotEvent === 'function') {
      nativeBridge.onChatbotEvent(message);
    }
  }

  /** Bridges chat-ui event stream payloads to the iframe host app. */
  private forwardChatEventsToParent(): void {
    if (!this.chatEventsService) {
      return;
    }

    this.chatEventsService.chatEvent$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(async (payload) => {
        this.postToParent({
          source: MessageSource.ChatBotWebapp,
          event: ChatbotEvent.ChatEvent,
          payload: await this.withUploadedFile(payload)
        });
      });
  }

  /** Bridges chat-ui download stream payloads to the iframe host app. */
  private forwardDownloadEventsToParent(): void {
    if (!this.appPermissionService || (!this.isMobileApp && !this.isCCU)) {
      return;
    }

    this.appPermissionService.fileDownloadBlob$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(async (fileData: { blob: Blob, fileName: string, mimeType: string }) => {

        // Converting the file blob to a base64 string to send via postMessage, as native bridges may not support Blob objects.
        const base64 = await this.fileUtilsService?.blobToBase64(fileData.blob);
        
        this.postToParent({
          source: MessageSource.ChatBotWebapp,
          event: ChatbotEvent.DownloadFile,
          payload: {
            fileName: fileData.fileName,
            mimeType: fileData.mimeType,
            base64
          }
        });
      });
  }

  /** Requests native microphone permission from mobile iframe and CCU native hosts. */
  private forwardMicPermissionRequestsToParent(): void {
    if (!this.appPermissionService || (!this.isMobileApp && !this.isCCU)) {
      return;
    }

    this.appPermissionService.requestAppMicrophonePermission()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        this.postToParent({
          source: MessageSource.ChatBotWebapp,
          event: ChatbotEvent.RequestMicrophonePermission
        });
      });
  }

  /** Requests native camera permission from mobile iframe and CCU native hosts. */
  private forwardCameraPermissionRequestsToParent(): void {
    if (!this.appPermissionService || (!this.isMobileApp && !this.isCCU)) {
      return;
    }

    this.appPermissionService.requestAppCameraPermissions()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        this.postToParent({
          source: MessageSource.ChatBotWebapp,
          event: ChatbotEvent.RequestCameraPermission
        });
      });
  }

  /** Adds the uploaded file from IndexedDB to dashboard navigation events when available. */
  private async withUploadedFile(payload: any): Promise<any> {
    if (payload?.actionType !== ChatActionType.NewDashboardNavigate) {
      return payload;
    }

    const uploadedFile = await this.getUploadedFileFromIndexedDb();
    return uploadedFile ? { ...payload, uploadedFile } : payload;
  }

  /** Reads the latest uploaded chat file stored by chat-ui in IndexedDB. */
  private getUploadedFileFromIndexedDb(): Promise<File | undefined> {
    return new Promise((resolve) => {
      const request = indexedDB.open(CHAT_INDEXED_DB_CONFIG.DB_NAME);

      request.onsuccess = (event: any) => {
        const db: IDBDatabase = event.target.result;
        if (!db.objectStoreNames.contains(CHAT_INDEXED_DB_CONFIG.STORE_NAME)) {
          db.close();
          resolve(undefined);
          return;
        }

        const tx = db.transaction(CHAT_INDEXED_DB_CONFIG.STORE_NAME, 'readonly');
        const getReq = tx.objectStore(CHAT_INDEXED_DB_CONFIG.STORE_NAME).get(CHAT_INDEXED_DB_CONFIG.FIELD_NAME);
        getReq.onsuccess = () => {
          db.close();
          resolve(getReq.result);
        };
        getReq.onerror = () => {
          db.close();
          resolve(undefined);
        };
      };
      request.onerror = () => resolve(undefined);
    });
  }

  /** Watches chat container class changes so the host app can mirror layout state. */
  private observeChatLayout(): void {
    const chatContainer = document.querySelector(`.${ChatLayoutClass.Container}`);
    if (!chatContainer) {
      window.setTimeout(() => this.observeChatLayout(), 50);
      return;
    }

    this.observedChatContainer = chatContainer;
    this.postChatLayoutState(chatContainer);
    this.chatContainerObserver = new MutationObserver(() => this.scheduleChatLayoutState(chatContainer));
    this.chatContainerObserver.observe(chatContainer, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true
    });
    if (typeof ResizeObserver === 'function') {
      this.chatContainerResizeObserver = new ResizeObserver(this.chatContainerResizeHandler);
      this.chatContainerResizeObserver.observe(chatContainer);
    }
    chatContainer.addEventListener('input', this.chatInputEventHandler, true);
    chatContainer.addEventListener('transitionend', this.chatTransitionEndHandler);
    window.addEventListener('resize', this.windowResizeHandler);
  }

  /** Posts layout state changes to the host app when maximize/collapse state changes. */
  private postChatLayoutState(chatContainer: Element, force = false): void {
    // Avoid posting layout state to the host app when running in CCU
    if (this.isCCU) return;
    
    const imagePreviewOpen = chatContainer.classList.contains(ChatLayoutClass.ImagePreviewOpen);
    const cameraDialogOpen = chatContainer.classList.contains(ChatLayoutClass.CameraDialogOpen);
    const overlayOpen = imagePreviewOpen || cameraDialogOpen;
    const maximized = chatContainer.classList.contains(ChatLayoutClass.Maximize) || overlayOpen;
    const collapsed = !overlayOpen && (chatContainer.classList.contains(ChatLayoutClass.Collapse) || chatContainer.classList.contains(ChatLayoutClass.MobileCollapse));
    const frame = this.getChatContainerFrame(chatContainer, maximized, collapsed, overlayOpen);
    const interactiveFrame = maximized && !overlayOpen ? this.getChatInteractiveFrame(chatContainer) : undefined;
    const interactiveFrameState = interactiveFrame
      ? `${interactiveFrame.left}:${interactiveFrame.top}:${interactiveFrame.width}:${interactiveFrame.height}`
      : '';
    const layoutState = `${maximized}:${collapsed}:${imagePreviewOpen}:${cameraDialogOpen}:${frame.width}:${frame.height}:${frame.topOffset ?? 0}:${interactiveFrameState}`;

    if (!force && layoutState === this.chatLayoutState) {
      return;
    }

    const payload = { maximized, collapsed, imagePreviewOpen, cameraDialogOpen, frame, ...(interactiveFrame ? { interactiveFrame } : {}) };

    if (!collapsed && !maximized && !overlayOpen) {
      this.lastExpandedLayoutPayload = payload;
    }

    this.chatLayoutState = layoutState;
    this.postToParent({
      source: MessageSource.ChatBotWebapp,
      event: ChatbotEvent.LayoutChange,
      payload
    });
  }

  private scheduleChatLayoutState(chatContainer: Element): void {
    // Avoid posting layout state to the host app when running in CCU
    if (this.isCCU) return;
    
    if (this.chatLayoutSettleTimeout) {
      window.clearTimeout(this.chatLayoutSettleTimeout);
    }
    const collapsed = this.isChatCollapsed(chatContainer);
    const expanding = this.chatLayoutCollapsed && !collapsed;
    this.chatLayoutCollapsed = collapsed;

    if (collapsed) {
      this.postToParent({
        source: MessageSource.ChatBotWebapp,
        event: ChatbotEvent.LayoutChange,
        payload: { collapsing: true }
      });
      this.chatLayoutSettleTimeout = window.setTimeout(() => this.postChatLayoutState(chatContainer), 350);
      return;
    }

    if (expanding) {
      if (this.lastExpandedLayoutPayload) {
        this.postToParent({
          source: MessageSource.ChatBotWebapp,
          event: ChatbotEvent.LayoutChange,
          payload: { ...this.lastExpandedLayoutPayload, expanding: true }
        });
      }
      this.chatLayoutSettleTimeout = window.setTimeout(() => this.postChatLayoutState(chatContainer), 350);
      return;
    }

    this.postChatLayoutState(chatContainer);
    this.chatLayoutSettleTimeout = window.setTimeout(() => this.postChatLayoutState(chatContainer, true), 350);
  }

  private postCurrentChatLayoutState(force = false): void {
    const chatContainer = document.querySelector(`.${ChatLayoutClass.Container}`);
    if (chatContainer) {
      this.postChatLayoutState(chatContainer, force);
    }
  }

  private isChatCollapsed(chatContainer: Element): boolean {
    return chatContainer.classList.contains(ChatLayoutClass.Collapse) || chatContainer.classList.contains(ChatLayoutClass.MobileCollapse);
  }

  private getChatContainerFrame(chatContainer: Element, maximized: boolean, collapsed: boolean, overlayOpen = false): { width: number; height: number; topOffset?: number } {
    const rect = chatContainer.getBoundingClientRect();
    const topOffset = Number.isFinite(rect.top) ? Math.max(0, rect.top) : 0;

    if (maximized) {
      return {
        width: window.innerWidth,
        height: window.innerHeight,
        topOffset: overlayOpen ? 0 : Math.floor(topOffset)
      };
    }

    const rightOffset = Math.max(0, window.innerWidth - rect.right);
    const bottomOffset = Math.max(0, window.innerHeight - rect.bottom);
    const frameHeight = Math.ceil((rect.height / 0.9) + bottomOffset);

    if (collapsed) {
      return {
        width: Math.ceil(rect.width + rightOffset),
        height: frameHeight,
        topOffset: Math.max(0, frameHeight - 80)
      };
    }

    return {
      width: Math.ceil(rect.width + rightOffset),
      height: frameHeight,
      topOffset: Math.floor(topOffset)
    };
  }

  private getChatInteractiveFrame(chatContainer: Element): { left: number; top: number; width: number; height: number } | undefined {
    const rect = chatContainer.getBoundingClientRect();
    const rectWidth = Number.isFinite(rect.width) ? rect.width : 0;
    const rectHeight = Number.isFinite(rect.height) ? rect.height : 0;
    const leftValue = Number.isFinite(rect.left) ? rect.left : rect.right - rectWidth;
    const topValue = Number.isFinite(rect.top) ? rect.top : rect.bottom - rectHeight;
    const rightValue = Number.isFinite(rect.right) ? rect.right : leftValue + rectWidth;
    const bottomValue = Number.isFinite(rect.bottom) ? rect.bottom : topValue + rectHeight;
    const left = Math.max(0, Math.floor(leftValue));
    const top = Math.max(0, Math.floor(topValue));
    const right = Math.min(window.innerWidth, Math.ceil(rightValue));
    const bottom = Math.min(window.innerHeight, Math.ceil(bottomValue));
    const width = Math.max(0, right - left);
    const height = Math.max(0, bottom - top);

    if (!width || !height) {
      return undefined;
    }

    return { left, top, width, height };
  }

  /** Handles config, permission, and building entity messages sent by supported host apps. */
  private onParentMessage(event: MessageEvent): void {
    const eventData = typeof event?.data === 'string' ? this.parseParentMessage(event.data) : event?.data;

    if (
      eventData?.source !== MessageSource.OccupantApp &&
      !(this.isCCU && eventData?.source === MessageSource.CcuApp) &&
      eventData?.source !== MessageSource.FacilisightApp &&
      eventData?.source !== MessageSource.PortalUiComponents
    ) {
      return;
    }

    // Synchronize tab navigation state from parent shell (indicates whether topbar/sidebar outline is visible)
    if (eventData?.type === '75F_TAB_NAV_STATE') {
      this.isTabNavActive = !!eventData?.active;
      return;
    }

    if (eventData?.event === ChatbotEvent.ConfigUpdate) {
      this.chatConfig = {
        ...this.chatConfig,
        ...(eventData?.payload?.config ?? {})
      };
      this.generativeWidgetReady = true;
      return;
    }

    if (eventData?.event === ChatbotEvent.UserInfo) {
      const userInfo = this.normalizeUserInfo(eventData?.payload);
      if (userInfo) {
        this.chatUserInfo = userInfo;
      }
      return;
    }

    if (eventData?.event === ChatbotEvent.CameraPermissionStatus) {
      this.appPermissionService?.receiveAppCameraPermissionStatus().next(!!eventData?.payload?.enabled);
    }

    if (eventData?.event === ChatbotEvent.MicrophonePermissionStatus) {
      this.appPermissionService?.receiveAppMicrophonePermissionStatus().next(!!eventData?.payload?.enabled);
    }

    if (eventData?.event === ChatbotEvent.BuildingEntityList || eventData?.type === ParentMessageType.BuildingEntityList) {
      this.buildingEntityList = Array.isArray(eventData?.payload) ? eventData.payload : [];
    }

    if (eventData?.event === ChatbotEvent.Restore) {
      // Host is re-opening an already-active AI Quote chat session; un-collapse it instead of reloading.
      this.aiQuoteChatComponent?.restoreFromMinimized();
    }
  }

  /** Safely parses stringified parent postMessage payloads. */
  private parseParentMessage(message: string): any {
    try {
      return JSON.parse(message);
    } catch {
      return null;
    }
  }

  /** Triggers the error message emitter to display error messages. */
  onErrorMessage(message: string): void {
    this.postToParent({
      source: MessageSource.ChatBotWebapp,
      event: ChatbotEvent.Error,
      payload: { message }
    });
  }

  /** Triggers the quote finalized event emitter to send the finalized quote to the parent. */
  onQuoteFinalized(quote: Quote): void {
    this.postToParent({
      source: MessageSource.ChatBotWebapp,
      event: ChatbotEvent.QuoteFinalized,
      payload: { quote }
    });
  }

  /**
   * Intercepts keyboard navigation and shortcut events within the iframe and forwards them to the parent shell.
   * - Tab / Shift+Tab: Allows keyboard focus to cycle between the embedded iframe and the host shell (topbar/sidebar).
   * - Escape: Closes active host panels/drawers or clears focus.
   * - ? / Shift+/: Opens the keyboard shortcut help dialog in the host portal.
   *
   * Form inputs, textareas, and contenteditable elements are bypassed to allow normal typing.
   */
  @HostListener('window:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    const isInput = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);

    // Do not intercept if user is typing inside chat message input or any text field
    if (isInput) {
      return;
    }

    const isTab = event.key === 'Tab' || event.code === 'Tab';
    const isEscape = event.key === 'Escape' || event.code === 'Escape';
    const isHelp = event.key === '?' || (event.shiftKey && (event.key === '/' || event.code === 'Slash'));

    if (isTab || isEscape || isHelp) {
      event.preventDefault();
      this.postToParent(JSON.stringify({
        type: '75F_KEYBOARD_SHORTCUT',
        key: event.key,
        code: event.code,
        shiftKey: event.shiftKey,
        altKey: event.altKey,
        ctrlKey: event.ctrlKey
      }));
    }
  }

  /**
   * Clears the tab navigation outline on the parent topbar/sidebar when the user clicks inside the iframe.
   * Only posts a message when `isTabNavActive` is true to avoid unnecessary postMessage traffic on every click.
   */
  @HostListener('window:mousedown')
  onMousedown(): void {
    if (this.isTabNavActive) {
      this.isTabNavActive = false;
      this.postToParent(JSON.stringify({ type: '75F_IFRAME_MOUSEDOWN' }));
    }
  }
}
